/**
 * Le chronomètre du sujet du dimanche (`core-api` §3.32, `web-pwa` §17.12).
 *
 * **Le serveur tient l'heure de départ et l'heure d'arrêt** ; l'écran ne fait
 * que les lire. Une horloge de tablette se règle à la main, et un chronomètre
 * tenu dans le navigateur recommencerait à zéro au premier rechargement de la
 * page — au milieu d'une épreuve de trois heures et demie.
 *
 * Partagé entre la coquille enfant et la V2, d'où `ui/` : l'enfant voit la même
 * heure de fin que le parent qui tient le chronomètre.
 *
 * Fonctions pures, exportées pour être testées sans monter d'écran.
 */

export type EtatDuChronometre = "pas_demarre" | "en_cours" | "arrete";

export function etatDuChronometre(sujet: {
  demarre_at?: string | null;
  arrete_at?: string | null;
}): EtatDuChronometre {
  if (!sujet.demarre_at) return "pas_demarre";
  return sujet.arrete_at ? "arrete" : "en_cours";
}

/** Les minutes écoulées depuis le départ, arrondies vers le bas ; figées à
 *  l'arrêt. Zéro si l'horodatage est illisible, plutôt qu'un `NaN` affiché. */
export function minutesEcoulees(
  demarreAt: string,
  maintenant: Date,
  arreteAt?: string | null,
): number {
  const debut = new Date(demarreAt).getTime();
  const fin = arreteAt ? new Date(arreteAt).getTime() : maintenant.getTime();
  if (Number.isNaN(debut) || Number.isNaN(fin)) return 0;
  return Math.max(0, Math.floor((fin - debut) / 60_000));
}

/** « 16 h 30 » : l'heure de fin prévue, à l'heure de l'appareil. */
export function finPrevue(demarreAt: string, dureeMin: number): string {
  const debut = new Date(demarreAt);
  if (Number.isNaN(debut.getTime())) return "";
  return heureDe(new Date(debut.getTime() + dureeMin * 60_000));
}

function heureDe(instant: Date): string {
  const heures = instant.getHours();
  const minutes = String(instant.getMinutes()).padStart(2, "0");
  return `${heures} h ${minutes}`;
}

/** « 15:00:00 » → « 15 h 00 ». Une heure d'horloge, sans fuseau : celle que
 *  l'élève a choisie, rendue telle quelle. */
export function heureLisible(heure: string | null | undefined): string | null {
  if (!heure) return null;
  const [h, m] = heure.split(":");
  if (h === undefined || m === undefined) return heure;
  return `${Number(h)} h ${m}`;
}

/** « 1 h 05 », « 45 min » : une durée officielle, dite comme on la lit. */
export function dureeLisible(minutes: number | null | undefined): string {
  if (minutes == null) return "durée non indiquée";
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste ? `${heures} h ${String(reste).padStart(2, "0")}` : `${heures} h`;
}

/** Ce qui se lit sous le chronomètre, pendant l'épreuve et après. Aucune
 *  remarque sur le temps pris : ni « en avance », ni « en retard ». */
export function ligneDuChronometre(
  sujet: {
    demarre_at?: string | null;
    arrete_at?: string | null;
    duree_officielle_min?: number | null;
  },
  maintenant: Date,
): string | null {
  const etat = etatDuChronometre(sujet);
  if (etat === "pas_demarre" || !sujet.demarre_at) return null;
  const ecoule = dureeLisible(minutesEcoulees(sujet.demarre_at, maintenant, sujet.arrete_at));
  if (etat === "arrete") return `Épreuve terminée — ${ecoule}.`;
  const duree = sujet.duree_officielle_min;
  if (duree == null) return `En cours depuis ${ecoule}.`;
  return `En cours depuis ${ecoule} — fin prévue à ${finPrevue(sujet.demarre_at, duree)} (${dureeLisible(duree)}).`;
}
